/**
 * "Push parent calibre to variants" for ONE parent, from its editor's Variants
 * panel — the brand-wide Apply Parent Calibre to Variants, scoped down.
 *
 * All the rules are the bulk enricher's, reused rather than restated:
 * `buildParentCalibreFixes` decides who qualifies (variant, no calibre, direct
 * parent, calibre neither deprecated nor a catch-all, tier copied only when the
 * calibre defines it) and `pushParentCalibresToVariants` writes. This file only
 * adapts a single parent's inputs to them, and says why nothing would happen
 * when nothing would.
 */
import type { CustomCalibreEntry } from '../../services/firebase';
import {
  isCatchAllCalibre,
  isDeprecatedCalibre,
  resolveDisplayCalibre,
} from '../../services/calibreResolution';
import type { WatchReference } from '../../types';
import { buildParentCalibreFixes, type ParentCalibreFix } from './parentCalibrePush';

export type VariantCalibrePushBlock =
  /** The parent names no calibre at all. */
  | 'parent-has-no-calibre'
  /** The parent names one by text only — this tool copies the `calibreId` link. */
  | 'parent-calibre-not-linked'
  /** `calibreId` points at a calibre document that does not exist. */
  | 'calibre-not-found'
  | 'calibre-deprecated'
  | 'calibre-catch-all';

export interface VariantCalibrePushPlan {
  /** Set when nothing can be pushed at all, with the reason. */
  blocked?: VariantCalibrePushBlock;
  /** The variants that would be filled, each with the calibre and tier it gets. */
  fixes: ParentCalibreFix[];
  /** Variants skipped because they already carry a calibre — never overwritten. */
  alreadyHaveCalibre: number;
  /** The tier being copied, when the parent has one the calibre defines. */
  tierName?: string;
}

/**
 * Plan a push for one parent.
 *
 * `entry` is the parent's linked calibre, fetched by `parent.calibreId` — one
 * read, rather than loading the catalogue the brand-wide tool scans with.
 *
 * ONE GUARD THE BULK TOOL DOES NOT NEED. `buildParentCalibreFixes` decides a
 * variant "has no calibre" partly by resolving its `calibreId` against the
 * calibres it was given. The bulk tool gives it the whole catalogue; here it only
 * has the parent's. A variant linked by id to a DIFFERENT calibre would therefore
 * look empty — and with the legacy string off, be overwritten. So any variant
 * carrying a `calibreId` is removed before the shared rule ever sees it.
 */
export function planVariantCalibrePush(input: {
  parent: WatchReference;
  variants: readonly WatchReference[];
  entry: CustomCalibreEntry | null;
  brand: { id?: string; name?: string };
}): VariantCalibrePushPlan {
  const { parent, variants, entry, brand } = input;
  const empty = (blocked: VariantCalibrePushBlock): VariantCalibrePushPlan => ({
    blocked,
    fixes: [],
    alreadyHaveCalibre: 0,
  });

  const parentCalibreId = (parent.calibreId ?? '').trim();
  if (!parentCalibreId) {
    // The same text lookup the bulk tool uses, so "has a calibre" means one thing.
    const hasText = !!resolveDisplayCalibre(parent);
    return empty(hasText ? 'parent-calibre-not-linked' : 'parent-has-no-calibre');
  }
  if (!entry) return empty('calibre-not-found');
  if (isDeprecatedCalibre(entry)) return empty('calibre-deprecated');
  if (isCatchAllCalibre(entry)) return empty('calibre-catch-all');

  const linkedElsewhere = variants.filter((v) => (v.calibreId ?? '').trim() !== '');
  const candidates = variants.filter((v) => (v.calibreId ?? '').trim() === '');

  const refsById = new Map<string, WatchReference>([[parent.id, parent]]);
  for (const v of candidates) refsById.set(v.id, v);

  const fixes = buildParentCalibreFixes(refsById, [entry], brand).filter(
    // The shared rule already requires the DIRECT parent; this keeps the plan
    // honest if a caller ever passes a variant of some other parent.
    (fix) => fix.parent.id === parent.id,
  );

  const tierId = fixes.find((f) => f.tierId)?.tierId;
  const tierName = tierId ? entry.data?.tiers?.find((t) => t.id === tierId)?.name : undefined;

  return {
    fixes,
    // Everything that is not being filled already had a calibre: either a link
    // (removed above) or a text value the shared rule saw and skipped.
    alreadyHaveCalibre: linkedElsewhere.length + (candidates.length - fixes.length),
    ...(tierName ? { tierName } : {}),
  };
}

/** Human wording for a block, shown in place of the preview. */
export function describeBlock(block: VariantCalibrePushBlock): string {
  switch (block) {
    case 'parent-has-no-calibre':
      return 'This reference has no calibre to push.';
    case 'parent-calibre-not-linked':
      return 'This reference names its calibre as text only. Link it to a calibre first — the push copies that link.';
    case 'calibre-not-found':
      return 'The linked calibre no longer exists.';
    case 'calibre-deprecated':
      return 'The linked calibre is deprecated, so it is not pushed onto more references.';
    case 'calibre-catch-all':
      return 'The linked calibre is a catch-all placeholder, so it is not pushed onto more references.';
  }
}
